import React, { useContext, useEffect, useRef } from 'react'
import classes from './Modal.module.scss';
import { ModalContext } from '../../store/modal-context';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Modal = ({ children }) => {
  const { closeModal } = useContext(ModalContext);
  const modalRef = useRef(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const node = modalRef.current;
    const focusables = node.querySelectorAll(FOCUSABLE_SELECTOR);
    focusables[0]?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeModal();
        return;
      }

      if (e.key !== 'Tab') return;

      const currentFocusables = Array.from(node.querySelectorAll(FOCUSABLE_SELECTOR));
      if (!currentFocusables.length) return;

      const first = currentFocusables[0];
      const last = currentFocusables[currentFocusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    // If the focused element disappears or gets disabled mid-interaction
    // (e.g. a submit button while a request is in flight), the browser
    // drops focus to <body> — which would silently break both the Tab
    // trap and Escape, since neither reaches this node once focus is
    // outside it. Reclaim focus whenever it lands outside the modal.
    const handleFocusOut = () => {
      requestAnimationFrame(() => {
        if (node.contains(document.activeElement)) return;
        const stillFocusable = node.querySelectorAll(FOCUSABLE_SELECTOR);
        stillFocusable[0]?.focus();
      });
    };

    document.addEventListener('keydown', handleKeyDown);
    node.addEventListener('focusout', handleFocusOut);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      node.removeEventListener('focusout', handleFocusOut);
      previouslyFocused?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className={classes.modal} ref={modalRef} role="dialog" aria-modal="true">
        {children}
    </section>
  )
}

export default Modal;
